import _ from "lodash";
import client from "lib/apolloClient/client";
import update from "immutability-helper";
import {
  updateNotificationIsReadForUser,
  updateNotificationIsRead,
  updateNotificationIsNew,
} from "modules/CommonMutations";
import {
  getUserNotificationCountFromCache,
  writeUserNotificationInFromCache,
  getNotificationFromCache,
  writeNotificationFragment,
  writeNotificationListInCache,
} from "modules/CommonGraphqlHelpers";
import ACLStore from "lib/aclStore";
import {
  getNotificationListV2FromCache,
  writeNotificationListV2InCache,
  getNotificationV2FromCache,
  writeNotificationV2Fragment,
} from "./NotificationCenterV2GraphqlHelpers";
import { getUserInfo } from "Login/modules/LoginModule/LoginUtils";
import { getCurriculumProgramFreeStatus } from "Platform/modules/PlatformModule";
import {
  getCategoryTabsForNotification,
  getNotificationCategory,
} from "modules/Services";

export const allNotificationsRead = ({ showOnlyUnread }) => {
  return async (dispatch, getState) => {
    const userInfo = getUserInfo();
    const state = getState();
    const userId = userInfo.id;
    const userEntityType = userInfo.userEntityType;
    const notificationCategory = getNotificationCategory();
    const userType = _.get(userInfo, "user_type");
    const isCurriculumProgramFree = getCurriculumProgramFreeStatus({ state });
    const categoryTabsInNotificationCenter = getCategoryTabsForNotification({
      isCurriculumProgramFree,
      userType,
    });
    const data =
      _.cloneDeep(
        getUserNotificationCountFromCache({
          id: userId,
          type: userEntityType,
          notificationCategoryFilter: categoryTabsInNotificationCenter,
        })
      ) || {};

    try {
      await client.mutate({
        mutation: updateNotificationIsReadForUser,
        variables: {
          input: { updatedBy: userId },
        },
        update: (
          cache,
          {
            data: {
              platform: { updateNotificationIsReadForUser },
            },
          }
        ) => {
          if (updateNotificationIsReadForUser) {
            _.forEach(notificationCategory, ({ filterValue }) => {
              const variables = {
                id: userId,
                type: userEntityType,
                filters: { showOnlyUnread },
                notificationCategoryFilter: filterValue,
              };
              const data = getNotificationListV2FromCache(variables);

              if (!_.isEmpty(data)) {
                const notificationEdges = _.get(data, "notification.edge", []);
                let clonedEdges = { ...notificationEdges };
                clonedEdges = _.map(clonedEdges, item => {
                  return { ...item, node: { ...item.node, isRead: true } };
                });

                if (!_.isEmpty(data.notification)) {
                  const updatedData = update(data, {
                    notification: { edge: { $set: clonedEdges } },
                  });

                  writeNotificationListV2InCache({
                    id: userId,
                    data: updatedData,
                    type: userEntityType,
                    filters: { showOnlyUnread },
                    notificationCategoryFilter: filterValue,
                  });
                }
              }
            });
          }
        },
      });
      const newCount = 0;

      // categorisedNewCount
      const categorisedNewCount = _.get(
        data,
        "notification.categorisedNewCount",
        []
      );
      let updatedCategorisedNewCount = categorisedNewCount;
      updatedCategorisedNewCount = _.map(
        updatedCategorisedNewCount,
        category => {
          return {
            ...category,
            count: 0,
          };
        }
      );
      writeUserNotificationInFromCache({
        id: userId,
        type: userEntityType,
        data: {
          ...data,
          notification: {
            ...(data.notification || {}),
            newCount,
            categorisedNewCount: updatedCategorisedNewCount,
          },
        },
        notificationCategoryFilter: categoryTabsInNotificationCenter,
      });
      await client.mutate({
        mutation: updateNotificationIsNew,
        variables: {
          input: {
            userId,
            updatedBy: userId,
            types: categoryTabsInNotificationCenter,
          },
        },
      });
    } catch (e) {
      writeUserNotificationInFromCache({
        id: userId,
        data,
        type: userEntityType,
      });
    }
  };
};

export const notificationReadOnClick = ({ notificationId }) => {
  return (dispatch, getState) => {
    const userInfo = getUserInfo();
    const userId = userInfo.id;
    client
      .mutate({
        mutation: updateNotificationIsRead,
        variables: {
          input: { id: notificationId, updatedBy: userId },
        },
        update: (
          cache,
          {
            data: {
              platform: { updateNotificationIsRead },
            },
          }
        ) => {
          if (updateNotificationIsRead) {
            const data = getNotificationV2FromCache(notificationId);
            const clonedData = { ...data };
            clonedData.isRead = true;
            writeNotificationV2Fragment({ notificationId, data: clonedData });
          }
        },
      })
      .then(res => {})
      .catch(err => {});
  };
};

export const isGlobalHeaderEnabled = () =>
  ACLStore.can("FeatureFlag:EnableGlobalHeader");

export const getNewValueWhenGlobalHeaderEnabled = (oldVal, newVal) => {
  return isGlobalHeaderEnabled() ? newVal : oldVal;
};

export const notificationCenterOpenMutation = ({
  userId,
  activeTabFilterValue,
}) => {
  return async (dispatch, getState) => {
    const state = getState();
    const userInfo = getUserInfo();
    const userEntityType = userInfo.userEntityType;

    const userType = userInfo.user_type;

    const isCurriculumProgramFree = getCurriculumProgramFreeStatus({ state });
    // get current category tabs visible in notification center
    const categoryTabsInNotificationCenter = getCategoryTabsForNotification({
      isCurriculumProgramFree,
      userType,
    });

    // get data from cache
    const data =
      _.cloneDeep(
        getUserNotificationCountFromCache({
          id: userId,
          type: userEntityType,
          notificationCategoryFilter: categoryTabsInNotificationCenter,
        })
      ) || {};

    try {
      const variables = {
        id: userId,
        type: userEntityType,
        filters: { showOnlyUnread: false },
        notificationCategoryFilter: activeTabFilterValue,
      };
      const notificationData = getNotificationListV2FromCache(variables);
      let notificationIds = [];
      let notificationNewCount = 0;

      if (!_.isEmpty(notificationData)) {
        const notificationEdges = _.get(
          notificationData,
          "notification.edge",
          []
        );
        const notifications = _.map(notificationEdges, ({ node }) => node);
        notificationIds = _.map(notifications, notification =>
          _.get(notification, "id")
        );
        if (_.size(notificationIds) === 0) return;
        notificationNewCount = _.reduce(
          notifications,
          (accumulator, notification) => {
            if (_.get(notification, "isNew")) {
              return accumulator + 1;
            }

            return accumulator;
          },
          0
        );
        let clonedEdges = { ...notificationEdges };
        clonedEdges = _.map(clonedEdges, item => {
          return { ...item, node: { ...item.node, isNew: false } };
        });

        if (!_.isEmpty(notificationData.notification)) {
          const updatedData = update(notificationData, {
            notification: { edge: { $set: clonedEdges } },
          });

          writeNotificationListV2InCache({
            id: userId,
            data: updatedData,
            type: userEntityType,
            filters: { showOnlyUnread: false },
            notificationCategoryFilter: activeTabFilterValue,
          });
        }
      }
      // new count
      let newCount = _.get(data, "notification.newCount", 0);

      // categorisedNewCount
      const categorisedNewCount = _.get(
        data,
        "notification.categorisedNewCount",
        []
      );

      let updatedCategorisedNewCount = categorisedNewCount;
      _.forEach(activeTabFilterValue, category => {
        const index = _.findIndex(
          updatedCategorisedNewCount,
          item => item.category === category
        );

        if (index >= 0) {
          // subtract from new count
          newCount -= _.get(updatedCategorisedNewCount[index], "count", 0)
            ? notificationNewCount
            : 0;
          const updatedCount =
            _.get(updatedCategorisedNewCount[index], "count", 0) -
              notificationNewCount >
            0
              ? _.get(updatedCategorisedNewCount[index], "count", 0) -
                notificationNewCount
              : 0;
          // update categorisedNewCount
          updatedCategorisedNewCount = update(updatedCategorisedNewCount, {
            [index]: {
              $set: {
                ...(categorisedNewCount[index] || {}),
                count: updatedCount,
              },
            },
          });
        }
      });

      // write updated categorised count and updated new count in cache
      writeUserNotificationInFromCache({
        id: userId,
        type: userEntityType,
        data: {
          ...data,
          notification: {
            ...(data.notification || {}),
            newCount,
            categorisedNewCount: updatedCategorisedNewCount,
          },
        },
        notificationCategoryFilter: categoryTabsInNotificationCenter,
      });

      // mutation
      await client.mutate({
        mutation: updateNotificationIsNew,
        variables: {
          input: {
            userId,
            updatedBy: userId,
            notificationIds: notificationIds,
          },
        },
      });
    } catch (e) {
      // on failure write old data in cache
      writeUserNotificationInFromCache({
        id: userId,
        data,
        type: userEntityType,
      });
    }
  };
};

export const notificationCenterReadMutation = ({ userId, notificationId }) => {
  return (dispatch, getState) => {
    client
      .mutate({
        mutation: updateNotificationIsRead,
        variables: {
          input: { id: notificationId, updatedBy: userId },
        },
        update: (
          proxy,
          {
            data: {
              platform: { updateNotificationIsRead },
            },
          }
        ) => {
          if (updateNotificationIsRead) {
            const data = getNotificationFromCache(notificationId);
            data.isRead = true;
            writeNotificationFragment({ notificationId, data });
          }
        },
      })
      .then(res => {})
      .catch(err => {});
  };
};

export const notificationCenterReadForUserMutation = userId => {
  return async (dispatch, getState) => {
    const userInfo = getUserInfo();
    const userEntityType = userInfo.userEntityType;

    try {
      await client.mutate({
        mutation: updateNotificationIsReadForUser,
        variables: {
          input: { updatedBy: userId },
        },
        update: (
          proxy,
          {
            data: {
              platform: { updateNotificationIsReadForUser },
            },
          }
        ) => {
          if (updateNotificationIsReadForUser) {
            _.forEach(getNotificationCategory(), category => {
              const variables = {
                id: userId,
                type: userEntityType,
                notificationCategoryFilter: category.filterValue,
              };
              const data = getNotificationListV2FromCache(variables);

              if (!_.isEmpty(data)) {
                //data.notification.edge.node.isRead = true
                let edges = { ..._.get(data, "notification.edge", []) };
                edges = _.map(edges, item => {
                  return { ...item, node: { ...item.node, isRead: true } };
                });

                if (data.notification) {
                  const updatedData = update(data, {
                    notification: { edge: { $set: edges } },
                  });

                  writeNotificationListInCache({
                    id: userId,
                    data: updatedData,
                    type: userEntityType,
                    notificationCategoryFilter: category.filterValue,
                  });
                }
              }
            });
          }
        },
      });
    } catch (e) {}
  };
};
